import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, TextField, Box, Typography, Checkbox, 
    FormControlLabel, CircularProgress, Divider, Grid
} from '@mui/material';
import { RoleService } from '../api/services/roleService';

export default function RoleFormModal({ open, onClose, onSave, editingRole }) {
    const [name, setName] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [availablePermissions, setAvailablePermissions] = useState([]);

    useEffect(() => {
        if (open) {
            // Reset and prefill
            if (editingRole) {
                setName(typeof editingRole.name === 'object' ? editingRole.name.name || '' : editingRole.name || '');
                setSelectedPermissions(editingRole.permissions || []);
            } else {
                setName('');
                setSelectedPermissions([]);
            }

            // Fetch permissions ONLY when modal opens
            const fetchPermissions = async () => {
                setIsLoadingPermissions(true);
                try {
                    const matrix = await RoleService.getPermissionsMatrix();
                    // Normalize permissions matrix to flat list
                    let permsList = [];
                    if (matrix.availablePermissions) {
                        permsList = matrix.availablePermissions;
                    } else if (Array.isArray(matrix)) {
                        permsList = matrix;
                    } else {
                        // Fallback for structured object
                        Object.values(matrix).forEach(categoryPerms => {
                            if (typeof categoryPerms === 'object' && categoryPerms !== null) {
                                Object.entries(categoryPerms).forEach(([key, value]) => {
                                    permsList.push(typeof value === 'string' ? value : key);
                                });
                            }
                        });
                    }
                    setAvailablePermissions(permsList);
                } catch (error) {
                    console.error("Failed to fetch permissions:", error);
                } finally {
                    setIsLoadingPermissions(false);
                }
            };

            fetchPermissions();
        }
    }, [open, editingRole]);

    const handleTogglePermission = (permValue) => {
        setSelectedPermissions(prev => 
            prev.includes(permValue) 
                ? prev.filter(p => p !== permValue)
                : [...prev, permValue]
        );
    };

    const handleSave = async () => {
        if (!name.trim()) return;
        setIsSaving(true);
        try {
            const cleanPermissions = selectedPermissions.filter(p => typeof p === 'string');
            await onSave({ name, permissions: cleanPermissions });
            onClose();
        } catch (error) {
            console.error("Save error", error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="md" fullWidth>
            <DialogTitle>
                {editingRole ? 'Edit Role' : 'Create Role'}
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Role Name"
                    fullWidth
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    sx={{ mb: 3 }}
                    disabled={isSaving}
                />

                <Typography variant="h6" gutterBottom>Permissions</Typography>
                <Divider sx={{ mb: 2 }} />

                {isLoadingPermissions ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary">Loading available permissions...</Typography>
                    </Box>
                ) : availablePermissions.length > 0 ? (
                    <Grid container spacing={1}>
                        {availablePermissions.map((perm) => (
                            <Grid item xs={12} sm={6} md={4} key={perm}>
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            checked={selectedPermissions.includes(perm)}
                                            onChange={() => handleTogglePermission(perm)}
                                            size="small"
                                            disabled={isSaving}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            {perm === '*' ? 'ALL (Wildcard)' : perm.replace(/_/g, ' ')}
                                        </Typography>
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Typography color="text.secondary">No permissions available to select.</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    disabled={isSaving || !name.trim() || isLoadingPermissions}
                    startIcon={isSaving && <CircularProgress size={16} color="inherit" />}
                >
                    {editingRole ? 'Update Role' : 'Create Role'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
